import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { PageContent } from '@/components/PageContent/PageContent'
import { TabsNav } from '@/components/TabsNav/TabsNav'
import { focusIds } from '@/constants/focus'
import { usePageOptionsUpdate } from '@/hooks/usePageOptionsUpdate'
import useStore from '@/store/useStore'
import { TabsProps } from '@/types/componentsPropsTypes'
import { TabType } from '@/types/servicesTypes/pageTypes'

const TabsPageRaw: FC<TabsProps> = ({ isPopup }) => {
  const focusIdPrefix = isPopup ? focusIds.PAGE_POPUP : ''

  const storeType = isPopup ? 'popup' : 'page'
  const { setTabIndex } = useStore((state) => state[storeType])
  const { page: pageData } = useStore((state) => state.page)
  const { pagePopup: pagePopupData } = useStore((state) => state.popup)
  const page = isPopup ? pagePopupData : pageData
  const tabs = page?.tabs
  const activeTabIndex = page?.tabIndex || 0
  const initFocusId = page?.focusId

  const [activeTab, setActiveTab] = useState<TabType>()
  const startFocusChild = page?.anchorId || initFocusId ? 1 : undefined

  const { resetPageOptions } = usePageOptionsUpdate(isPopup)

  useEffect(() => {
    tabs?.length && setActiveTab(tabs[activeTabIndex])
  }, [tabs, activeTabIndex])

  const handleActiveTabChange = useCallback(
    (tab: TabType, index: number) => {
      setActiveTab(tab)
      setTabIndex(index)
      resetPageOptions()
    },
    [setTabIndex],
  )

  const renderedTabNav = useMemo(
    () =>
      tabs ? (
        <TabsNav
          focusIdPrefix={focusIdPrefix + page.id}
          pageId={page.id}
          tabs={tabs}
          tabIndex={activeTabIndex}
          activeTab={activeTab}
          setActiveTab={handleActiveTabChange}
        />
      ) : null,
    [
      focusIdPrefix,
      page?.id,
      tabs,
      activeTabIndex,
      activeTab,
      handleActiveTabChange,
    ],
  )

  return (
    <PageContent
      isPopup={isPopup}
      activeTabIndex={activeTabIndex}
      renderedTabNav={renderedTabNav}
      startFocusChild={startFocusChild}
    />
  )
}

export const TabsPage = memo(TabsPageRaw)
